import { get, getAll } from '@bigyouth/base'
import * as L from 'leaflet'
import 'proj4leaflet'
import countriesData from '../../../../public/assets/front/countries-geo-cleaned.json'
import worldData from '../../../../public/assets/front/world.json'
import instantsearch from 'instantsearch.js'
import algoliasearch from 'algoliasearch/lite'

import { configure, refinementList } from 'instantsearch.js/es/widgets'
import { connectHits } from 'instantsearch.js/es/connectors'

import customClear from '../utils/Lists/customClear'
import customCurrentRefinements from '../utils/Lists/customCurrentRefinements'
import customStats from '../utils/Lists/customStats'

export default {
  name: 'projects-map',

  baseUrlProjectsPage: null,
  filtersUrl: '',

  ui: {
    'panels[]': '[js-projects-map-panel]',
    'togglePanel[]': '[js-projects-map-toggle-panel]',
    'closeFilters[]': '[js-projects-map-close-filters]',
    'numberItems[]': '[js-projects-map-number-items]',
    refine: '[js-projects-map-refine]',
    actives: '[js-projects-map-actives]',
    currentFilters: '[js-projects-map-current]',
    clear: '[js-projects-map-clear]',
    clearMobile: '[js-projects-map-clear-mobile]',
    'backFilters[]': '[js-algolia-list-back-filters]',
    'refinementsList[]': '[js-projects-map-refinements-list]',
  },

  events: [
    {
      on: 'click',
      target: 'togglePanel',
      handle ({ target }) {
        get(`[js-projects-map-panel][data-panel="${target.dataset.panel}"]`).classList.add('is-visible')
      },
    },
    {
      on: 'click',
      target: 'refine',
      handle ({ target }) {
        this.saveCurrentSearchState()
      },
    },
    {
      on: 'click',
      target: 'closeFilters',
      handle ({ target }) {
        // reset search to previous state since apply was not hit
        if (target.classList.contains('filters__close-button')) {
          this.unsetFilters()
        }

        setTimeout(() => {
          this.ui.panels.forEach((panel) =>
            panel.classList.remove('is-visible'),
          )
        }, 600)
      },
    },
    {
      on: 'click',
      target: 'backFilters',
      handle () {
        getAll('[js-projects-map-panel].is-visible').forEach((panel) =>
          panel.classList.remove('is-visible'),
        )
      },
    },
  ],

  // map functions
  // /////////////////////

  // country color based on nbProjects
  getCountryColor (n) {
    return n > 5 ? '#0058A2' : n > 0 ? '#04B6D5' : '#D9E6F1'
  },

  // display popup on hover (and remove if country has 0 project)
  hoverCountry (e) {
    const layer = e.target
    const popLocation = new L.LatLng(e.latlng.lat, e.latlng.lng)

    if (layer.feature.properties.nbProjects > 0) {
      let projectsPageCountryUrl = `${this.baseUrlProjectsPage}?country%5B0%5D=${encodeURI(layer.feature.properties[`name_${this.props.locale}`])}${this.filtersUrl}`
      let name = layer.feature.properties[`name_${this.props.locale}`]
      let classPopup = 'is-reg'

      if (['FR', 'CD', 'PG'].includes(layer.feature.properties.iso_a2)) {
        classPopup = 'is-big'
      }

      // case world
      if (layer.feature.properties.iso_a2 === 'WORLD') {
        let filtersParam = this.filtersUrl

        if (filtersParam.includes('continent')) {
          let arrayFilters = filtersParam.split('&')
          arrayFilters = arrayFilters.filter(item => !item.includes('continent'))

          filtersParam = arrayFilters.join('&')
        }

        projectsPageCountryUrl = `${this.baseUrlProjectsPage}?continent%5B0%5D=${encodeURI(layer.feature.properties[`name_${this.props.locale}`])}${filtersParam}`
      }

      if (name === 'France' || name === 'Francia') {
        if (this.props.locale === 'fr') {
          name = 'France métropolitaine & DROM-COM'
        } else if (this.props.locale === 'en') {
          name = 'Metropolitan France & DROM-COM'
        } else if (this.props.locale === 'es') {
          name = 'Francia Metropolitana y DROM-COM'
        }
      }

      L.popup({ className: classPopup }).setLatLng(popLocation)
        .setContent(`<div class="flex test"><svg width="24" height="24" class="icon-dark-blue flex-shrink-0 mr-2">
        <use href="#icon-pin" xlink:href="#icon-pin"></use></svg><div><p class="projects-map__title-popin">${name}</p><p class="text-14 md:text-16 text-dark-blue">${layer.feature.properties.nbProjects} ${layer.feature.properties.nbProjects > 1 ? config.trads['list.project.plural'] : config.trads['list.project.singular']}</p><a class="cta" href="${projectsPageCountryUrl}">${layer.feature.properties.nbProjects > 1 ? config.trads.see_projects : config.trads.see_project}</a></div></div>`)
        .openOn(this.map)

      layer.openPopup()

      if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
        layer.bringToFront()
      }
    } else {
      this.map.closePopup()
    }
  },

  // add events on each country
  onEachFeature (feature, layer) {
    const featureOptions = {
      mouseover: this.hoverCountry,
      click: this.hoverCountry,
    }

    layer.on(featureOptions)
  },

  // styles for the country layer
  style (feature) {
    return {
      stroke: true,
      weight: 1,
      color: '#FFFFFF',
      fill: true,
      fillColor: this.getCountryColor(feature.properties.nbProjects),
      fillOpacity: 1,
    }
  },

  // clearMap between filters
  clearMap () {
    this.map.eachLayer(layer => {
      this.map.removeLayer(layer)
    })
  },

  // add geojson to map
  populateMap () {
    // add geojson datas layer to map
    L.geoJson(countriesData, {
      style: this.style,
      onEachFeature: this.onEachFeature,
    }).addTo(this.map)

    L.geoJSON(worldData, {
      onEachFeature: this.onEachFeature,
      pointToLayer: (feature, coord) => {
        const circlebuff = new L.Circle(coord, 750000, { color: this.getCountryColor(feature.properties.nbProjects), fillColor: this.getCountryColor(feature.properties.nbProjects), fillOpacity: 1, weight: 10 })
        return circlebuff
      },
    }).addTo(this.map)

    const WorldIconClass = L.Icon.extend({
      options: {
        iconUrl: 'data:image/svg+xml;base64,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',
        iconSize: [40, 40], // size of the icon
      },
    })

    const worldIcon = new WorldIconClass({ iconSize: [40, 40] })

    const markerWorld = L.marker([16.55, 164.5], { icon: worldIcon }).addTo(this.map)

    this.map.on('zoomend', () => {
      let actualZoom = this.map.getZoom()
      actualZoom = actualZoom === 0 ? 0.5 : actualZoom
      const newSize = 40 * actualZoom

      if (actualZoom > 2) {
        markerWorld.setLatLng([17, 164.5])
      } else {
        markerWorld.setLatLng([16.55, 164.5])
      }

      const resizedIcon = new WorldIconClass({ iconSize: [newSize, newSize] })
      markerWorld.setIcon(resizedIcon)
    })
  },

  // algolia functions
  // get nbProjects by country based on algolia hits return
  renderHitsAlgolia (renderOptions, isFirstRender) {
    const { hits } = renderOptions

    if (!isFirstRender) {
      const aggregatedArray = []

      hits.forEach(hit => {
        hit.countries.forEach(country => {
          if (aggregatedArray[country.title] === undefined) {
            aggregatedArray[country.title] = 1
          } else {
            aggregatedArray[country.title] += 1
          }
        })

        const isWorld = ['monde', 'mundo', 'world'].includes(hit.continent.toLowerCase())

        if (hit.countries.length === 0 || isWorld) {
          let continentLabel = hit.continent.toLowerCase()
          continentLabel = continentLabel.charAt(0).toUpperCase() + continentLabel.slice(1)

          if (aggregatedArray[continentLabel] === undefined) {
            aggregatedArray[continentLabel] = 1
          } else {
            aggregatedArray[continentLabel] += 1
          }
        }
      })

      // update json datas with nbProjects
      countriesData.features.forEach(feature => {
        const nbProjects = aggregatedArray[feature.properties[`name_${this.props.locale}`]] ?? 0
        feature.properties.nbProjects = nbProjects
      })

      worldData.features.forEach(feature => {
        const nbProjects = aggregatedArray[feature.properties[`name_${this.props.locale}`]] ?? 0
        feature.properties.nbProjects = nbProjects
      })

      // get base URL for button in countries popup
      setTimeout(() => {
        this.filtersUrl = window.location.search

        if (this.filtersUrl !== '' && this.filtersUrl !== null) {
          this.filtersUrl = `&${this.filtersUrl.slice(1)}`
        }
      }, 2000)

      this.populateMap()
    }
  },

  // save currentfilters for before opening filters modal
  saveCurrentSearchState () {
    this.savedSearchState = this.search.renderState[`${this.search.indexName}`].currentRefinements.items
  },

  // unset filters if close button is hit
  unsetFilters () {
    const searchState = this.search.renderState[`${this.search.indexName}`].refinementList

    // if filters saved compare and uncheck or checl items
    if (this.savedSearchState.length > 0) {
      this.savedSearchState.forEach(item => {
        const widget = searchState[item.attribute]

        widget.items.forEach(filterItem => {
          const inSavedSearch = item.refinements.filter(refinement => refinement.value === filterItem.value).length > 0

          if ((filterItem.isRefined && !inSavedSearch) || (!filterItem.isRefined && inSavedSearch)) {
            widget.refine(filterItem.value)
          }
        })
      })
    // if no filters saved, remove all checked items
    } else {
      this.ui.clear.querySelector('button').click()
    }
  },

  componentDidMount () {
    this.baseUrlProjectsPage = this.props.locale === 'en' ? '/en/our-current-projects' : this.props.locale === 'es' ? '/es/nuestros-proyectos-actuales' : '/nos-projets-en-cours'

    // init algolia
    const indexName = `${config.algolia.env}_pages`

    const searchClient = algoliasearch(
      process.env.ALGOLIA_APP_ID,
      process.env.ALGOLIA_API_KEY,
    )

    this.search = instantsearch({
      indexName: indexName,
      searchClient: searchClient,
      routing: {
        stateMapping: {
          stateToRoute (uiState) {
            const indexUiState = uiState[indexName]

            return {
              theme: indexUiState.refinementList && indexUiState.refinementList['thematics.name'],
              country: indexUiState.refinementList && indexUiState.refinementList['countries.title'],
              continent: indexUiState.refinementList && indexUiState.refinementList.continent,
              expertises: indexUiState.refinementList && indexUiState.refinementList.expertises,
            }
          },
          routeToState (routeState) {
            return {
              [indexName]: {
                refinementList: {
                  continent: routeState.continent,
                  expertises: routeState.expertises,
                  'countries.title': routeState.country,
                  'thematics.name': routeState.theme,
                },
              },
            }
          },
        },
      },
    })

    // custom render hits
    const customHitsAlgolia = connectHits(this.renderHitsAlgolia)

    // add widgets
    this.search.addWidgets([
      // global
      configure({
        hitsPerPage: 500,
        filters: `status:published AND locale:${config.locale} AND type:project`,
      }),
      customHitsAlgolia(),
      // stats : in title, buttons etc
      customStats({
        type: 'project',
        numberItems: this.ui.numberItems,
      }),
      // current refinements list
      customCurrentRefinements({
        container: this.ui.currentFilters,
        currentTheme: null,
        currentSubtheme: null,
        currentSubthemeCount: null,
        suggestionsWrap: null,
        actives: this.ui.actives,
      }),
      // clear all refinements
      customClear({
        container: this.ui.clear,
      }),
      // clear all refinements mobile
      customClear({
        container: this.ui.clearMobile,
      }),
    ])

    // list of tags (city, thematics, countries etc ....)
    this.ui.refinementsList.forEach(list => {
      this.search.addWidgets([
        refinementList({
          container: list,
          limit: 200,
          attribute: list.dataset.attribute,
          sortBy (a, b) {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          },
          cssClasses: {
            root: 'is-themes',
          },
        }),
      ])
    })

    this.search.start()

    // init crs to have not plane map
    const crs = new L.Proj.CRS(
      'ESRI:54030',
      '+proj=robin +lon_0=0 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs',
      {
        resolutions: [65536, 32768, 16384, 8192, 4096, 2048, 1024, 512, 256, 128],
        origin: [0, 0],
      },
    )

    // init map with zoom and crs / center
    this.map = L.map('projects-map', {
      center: window.innerWidth < 768 ? [40, 30] : [25, 20],
      crs: crs,
      zoom: 1,
      maxZoom: 5,
      continuousWorld: false,
      noWrap: true,
      zoomControl: true,
      scrollWheelZoom: false,
    })
  },
}
